import cn from 'classnames'
import { getEltPageLink } from 'legacy/lut/components/widgets/public/partner360/ELT'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { AbsolutePopup, TPopupVertical, TVisibilityTrigger } from 'core-components/library/popup'
import { getQbrPageLink } from 'partner360/components/QBR'

import { getPartnerTypeByPartnerAccess } from '../../helpers/getPartnerTypeByPartnerAccess'
import { TRANSLATION_KEYS } from '../../TranslationKeys'

import styles from './ActionsMenu.module.scss'

interface IAction {
  key: string
  title: string
  href: string
}

interface IActionsMenuProps {
  isShowElt: boolean
  isShowQbr: boolean
  isShowSobTrends: boolean
  partnerId: string
  programType: string
  fYear: number
  eltGroupUIID?: Nullable<string>
}

const getSobTrendsPageLink = (partnerId: string, programType: string) => {
  return `/page/jbp-sob-trends/pid/${partnerId}/ptype/${programType}`
}

export const ActionsMenu = ({
  isShowElt,
  isShowQbr,
  isShowSobTrends,
  partnerId,
  programType,
  fYear,
  eltGroupUIID,
}: IActionsMenuProps) => {
  const partnerAccess = useSelector(selectPartnerAccess)

  const actions = useMemo<IAction[]>(() => {
    const items: IAction[] = []
    const rtm = getPartnerTypeByPartnerAccess(partnerAccess)

    if (isShowElt) {
      items.push({
        key: 'elt',
        title: TRANSLATION_KEYS.eltOpener,
        href: getEltPageLink(partnerId, fYear, eltGroupUIID),
      })
    }

    if (isShowQbr && rtm) {
      items.push({
        key: 'qbr',
        title: TRANSLATION_KEYS.qbrOpener,
        href: getQbrPageLink(rtm, partnerId),
      })
    }

    if (isShowSobTrends) {
      items.push({
        key: 'sob-trends',
        title: TRANSLATION_KEYS.sobTrends,
        href: getSobTrendsPageLink(partnerId, programType),
      })
    }

    return items
  }, [
    isShowElt,
    isShowQbr,
    isShowSobTrends,
    partnerAccess,
    partnerId,
    fYear,
    eltGroupUIID,
    programType,
  ])

  const handleActionClick = useCallback((href: string) => {
    window.open(href, '_blank', 'noreferrer')
  }, [])

  if (!actions.length) {
    return null
  }

  return (
    <AbsolutePopup
      verticalDirection={TPopupVertical.Bottom}
      verticalStartPosition={TPopupVertical.Bottom}
      visibilityTrigger={TVisibilityTrigger.Click}
      popupContent={() => (
        <div className={styles.popup}>
          {actions.map(({ key, title, href }) => (
            <button
              key={key}
              type="button"
              className={styles.menuItem}
              onClick={() => handleActionClick(href)}
            >
              <span className={styles.menuItemText}>
                <Translation text={title} />
              </span>

              <i className={cn('kbase kbase-arrow-right', styles.arrowIcon)} />
            </button>
          ))}
        </div>
      )}
    >
      <Button
        icon="kbase kbase-chevron-bottom"
        iconPosition="end"
        variant="outlined"
        className={styles.btn}
      >
        <Translation text={TRANSLATION_KEYS.actions} />
      </Button>
    </AbsolutePopup>
  )
}
